import {
  CheckOutlined,
  ClockCircleOutlined,
  CloseOutlined,
  ExclamationOutlined,
  EyeInvisibleOutlined,
  InfoCircleOutlined,
} from '@ant-design/icons';
import { Modal, Radio, Spin, message } from 'antd';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useState } from 'react';
import { RemindMuteAction } from '@/proto/resource-manager';
import type { IGetRemindMuteRsp, ISetRemindMuteReq } from '@/service/resource-package';
import { resourcePackageService } from '@/service/resource-package';
import styles from './index.module.scss';

/** 静音选项的「逻辑值」（区分：后端 action + close-only 本地不调接口） */
type OptionKey = 'read' | 'snooze' | 'close-only';

/** 合法的 snooze days 枚举：1/3/7（对应截图三段 pill） */
type SnoozeDay = 1 | 3 | 7;

/** ---- Props / Ref 接口（forwardRef 对外只暴露 open()，禁止 visible prop 控制）---- */
/** 横幅场景（决定 Modal desc 文案 + 标题 icon 颜色）：
 *  - expire  = 套餐即将到期（WILL_EXPIRE 或 免费版 WillExpire）【橙】
 *  - exceed  = 余额即将耗尽（WILL_EXCEED 上报量余量告警）【橙】
 *  - arrears = 已欠费（HAS_ALREADY_EXCEED 红色已欠费）【红】
 */
export type RemindScene = 'expire' | 'exceed' | 'arrears';

export interface IMuteModalProps {
  /** 当前产品 ID，注入到 Get/SetRemindMute 请求 body + headers（双保险） */
  productId: string;
  /** 横幅场景类型：决定 desc 文案 + 标题 icon 颜色（3 选 1） */
  remindScene: RemindScene;
  /**
   * 确定成功 / 「仅关闭横幅」后回调：父组件根据当前展示的横幅分支 hide 对应 state。
   */
  onCloseSuccess: () => void;
}

export interface IMuteModalRef {
  /** 打开静音弹窗：展示 Modal → 异步请求 GetRemindMute 回填上次选择 */
  open: () => void;
}

/** ---- scene → 文案 + 标题 icon 颜色（单一真源 mapping，保持切换即时一致） ---- */
interface SceneConfig {
  /** desc under title（支持 JSX 以突出关键片段，如欠费场景标红） */
  desc: React.ReactNode;
  /** 标题左侧圆形淡色底（tint） */
  iconBg: string;
  /** 标题左侧图标实色 */
  iconColor: string;
  /** Modal 标题文字（右上角标题随场景） */
  title: string;
}

const SCENE_CONFIG: Record<RemindScene, SceneConfig> = {
  expire: {
    desc: '套餐到期后数据将停止上报。请选择本次关闭后的提醒方式：',
    iconBg: 'rgba(255, 125, 0, 0.18)',
    iconColor: '#ff7d00',
    title: '关闭套餐即将到期提醒',
  },
  exceed: {
    desc: '余额耗尽后上报量将停止抵扣。请选择本次关闭后的提醒方式：',
    iconBg: 'rgba(255, 125, 0, 0.18)',
    iconColor: '#ff7d00',
    title: '关闭余额即将耗尽提醒',
  },
  arrears: {
    desc: (
      <>
        当前产品<span className={styles['mute-modal-desc-strong']}>已欠费并停止上报</span>。请选择本次关闭后的提醒方式：
      </>
    ),
    iconBg: 'rgba(245, 63, 63, 0.18)',
    iconColor: '#f53f3f',
    title: '关闭欠费提醒',
  },
};

export const MuteModal = forwardRef<IMuteModalRef, IMuteModalProps>((
  { productId, remindScene, onCloseSuccess },
  ref,
) => {
  /** ---- 本地状态 ---- */
  const [visible, setVisible] = useState(false);
  /** GetRemindMute 加载中：Radio.Group disabled 防止回填前用户点选后被覆盖 */
  const [getLoading, setGetLoading] = useState(false);
  /** SetRemindMute 提交中：Modal 确定按钮 loading 防止重复提交 */
  const [submitLoading, setSubmitLoading] = useState(false);

  /* 无默认选中：值仅来自 GetRemindMute 回填或用户手动选择；查询无数据/失败时保持不选中 */
  const [selectedOption, setSelectedOption] = useState<OptionKey | null>(null);
  /* 静默天数同样无默认：仅来自 snooze_days 回填或用户点选，避免「1天」pill 无数据时蓝色高亮像默认选中 */
  const [snoozeDays, setSnoozeDays] = useState<SnoozeDay | null>(null);

  /** ---- 派生：根据 remindScene 取 desc/iconBg（useMemo 避免每帧重算字面量） ---- */
  const cfg = useMemo<SceneConfig>(() => SCENE_CONFIG[remindScene], [remindScene]);

  const READ_STOP = RemindMuteAction.REMIND_MUTE_ACTION_READ_STOP;
  const SNOOZE = RemindMuteAction.REMIND_MUTE_ACTION_SNOOZE;

  /** ---- 状态重置（关闭后清理残留，下次 open 重新 Get + 回填） AC-7 ---- */
  useEffect(() => {
    if (!visible) {
      setSelectedOption(null);
      setSnoozeDays(null);
    }
  }, [visible]);

  /** ---- 选中「稍后提醒」时默认选中 1 天（用户手选或接口回填 snooze 都会触发） ---- */
  useEffect(() => {
    if (selectedOption === 'snooze' && snoozeDays === null) {
      setSnoozeDays(1);
    }
  }, [selectedOption, snoozeDays]);

  /** ---- 回填 Modal：GetRemindMute → mutes[] 中按 productId 找当前产品的 action ---- */
  const loadMuteState = useCallback(async (pid: string) => {
    setGetLoading(true);
    try {
      const resp = await resourcePackageService.getRemindMute({ productIds: [pid] }) as IGetRemindMuteRsp;
      const baseRet = resp?.baseRsp?.retCode ?? resp?.base_rsp?.retCode ?? 0;
      if (baseRet !== 0) {
        message.error(resp?.baseRsp?.retMsg || resp?.base_rsp?.retMsg || '获取提醒静音设置失败');
        return; /* 失败：保持不选中，不 block 用户手动选择 */
      }

      /* proto 响应为 mutes 数组（仅未过期记录）；snake/camel 两种 JSON 字段名都兼容 */
      const list = resp?.mutes || [];
      const info = list.find(m => (m?.productId || m?.product_id) === pid) || list[0];
      const action = info?.action;

      if (action === READ_STOP) {
        setSelectedOption('read');
      } else if (action === SNOOZE) {
        setSelectedOption('snooze');
        /* proto v2 回传 snooze_days：白名单校验（仅 1/3/7 合法）后回填，非法值保持不选中 */
        const days = info?.snoozeDays ?? info?.snooze_days;
        const validSnooze: SnoozeDay[] = [1, 3, 7];
        if (typeof days === 'number' && validSnooze.includes(days as SnoozeDay)) {
          setSnoozeDays(days as SnoozeDay);
        }
      }
      /* 其它 action 值（UNSPECIFIED / 空 / 未知）：不覆盖，保持不选中 */
    } catch (e) {
      message.error('获取提醒静音设置失败');
    } finally {
      setGetLoading(false);
    }
  }, [READ_STOP, SNOOZE]);

  /** ---- 提交：SetRemindMute（read/snooze）或 close-only 不调接口 ---- */
  const handleOk = useCallback(async () => {
    /* 无选中不允许提交（确定按钮已 disabled，此处双保险） */
    if (!selectedOption) return;

    /* close-only：纯本地关闭，不调后端 */
    if (selectedOption === 'close-only') {
      setVisible(false);
      onCloseSuccess();
      return;
    }

    const action: ISetRemindMuteReq['action'] = selectedOption === 'read'
      ? READ_STOP
      : SNOOZE;
    const req: ISetRemindMuteReq = selectedOption === 'snooze'
      ? { productId, action, snoozeDays: snoozeDays as SnoozeDay }
      : { productId, action };

    setSubmitLoading(true);
    try {
      const resp = await resourcePackageService.setRemindMute(req);
      const retCode: number | undefined = (resp as any)?.baseRsp?.retCode;
      if (retCode !== undefined && retCode !== 0) {
        message.error((resp as any)?.baseRsp?.retMsg || '设置提醒静音失败');
        return; /* 失败：Modal 不关闭、横幅不 hide */
      }
      setVisible(false);
      onCloseSuccess();
    } catch (e) {
      message.error('设置提醒静音失败');
    } finally {
      setSubmitLoading(false);
    }
  }, [selectedOption, READ_STOP, SNOOZE, productId, snoozeDays, onCloseSuccess]);

  /** ---- 取消 / 右上角 X 关闭 ---- */
  const handleCancel = useCallback(() => {
    setVisible(false);
  }, []);

  /** ---- forwardRef 对外暴露 open()：唯一入口 ---- */
  useImperativeHandle(ref, () => ({
    open() {
      if (getLoading || submitLoading) return; /* 防重入 */
      setVisible(true);
      if (productId) {
        loadMuteState(productId);
      }
    },
  }), [getLoading, submitLoading, productId, loadMuteState]);

  /* =========================================================
   * 3 个选项 JSX（className 与 SCSS 精确对齐）
   * ========================================================= */
  const renderOption = (
    key: OptionKey, title: string, sub: string,
    Icon: React.FC<any> | React.ComponentType<any>,
    extra?: React.ReactNode,
  ) => (
    <Radio
      value={key}
      className={`${styles['mute-modal-option']} ${selectedOption === key ? styles['mute-modal-option--selected'] : ''}`}
    >
      <div className={styles['mute-modal-option-body']}>
        <span className={`${styles['mute-modal-option-iconBadge']} ${styles[`mute-modal-option-iconBadge--${key}`]}`}>
          <Icon className={styles['mute-modal-option-icon']} />
        </span>
        <div className={styles['mute-modal-option-text']}>
          <div className={styles['mute-modal-option-title']}>{title}</div>
          <div className={styles['mute-modal-option-sub']}>{sub}</div>
          {extra}
        </div>
      </div>
    </Radio>
  );

  const SNOOZE_DAYS: SnoozeDay[] = [1, 3, 7];
  const snoozeDisabled = selectedOption !== 'snooze' || getLoading || submitLoading;
  const snoozeExtra = (
    <div className={styles['mute-modal-snooze-row']}>
      <span className={styles['mute-modal-snooze-label']}>静默时长</span>
      <span className={styles['mute-modal-snooze-days']}>
        {SNOOZE_DAYS.map((d) => {
          const active = snoozeDays === d;
          return (
            <span
              key={d}
              role="button"
              tabIndex={0}
              aria-disabled={snoozeDisabled}
              className={`${styles['mute-modal-snooze-pill']} ${active ? styles['mute-modal-snooze-pill--active'] : ''} ${snoozeDisabled ? styles['mute-modal-snooze-pill--disabled'] : ''}`}
              onClick={() => {
                if (snoozeDisabled) return;
                setSnoozeDays(d);
              }}
            >
              {d}天
            </span>
          );
        })}
      </span>
    </div>
  );

  /* =========================================================
   * Render（AntD 4.x API: Modal visible=）
   * 不使用 Modal 内置 header/close：标题行 + 关闭 X 全部自定义渲染在 body 顶部
   * ========================================================= */
  const headerNode = (
    <div className={styles['mute-modal-header']}>
      <span
        className={styles['mute-modal-title-icon']}
        style={{ background: cfg.iconBg }}
      >
        <span
          className={styles['mute-modal-title-iconCore']}
          style={{ background: cfg.iconColor }}
        >
          <ExclamationOutlined className={styles['mute-modal-title-iconMark']} />
        </span>
      </span>
      <span className={styles['mute-modal-title-text']}>{cfg.title}</span>
      <CloseOutlined className={styles['mute-modal-close']} onClick={handleCancel} />
    </div>
  );

  return (
    <Modal
      visible={visible}
      title={null}
      closable={false}
      onOk={handleOk}
      onCancel={handleCancel}
      confirmLoading={submitLoading}
      okText="确定"
      cancelText="取消"
      okButtonProps={{ type: 'primary', disabled: !selectedOption }}
      width={520}
      destroyOnClose
      maskClosable={false}
      className={styles['mute-modal-root']}
    >
      {headerNode}

      <div className={styles['mute-modal-desc']}>
        {cfg.desc}
      </div>

      <Spin spinning={getLoading} tip="加载中...">
        <Radio.Group
          value={selectedOption}
          onChange={e => !getLoading && !submitLoading && setSelectedOption(e.target.value)}
          className={styles['mute-modal-option-group']}
          disabled={getLoading || submitLoading}
        >
          {renderOption(
            'read',
            '我知道了，不再提醒',
            '标记已读，7 天内不再向我推送邮件 / 短信',
            CheckOutlined,
          )}
          {renderOption(
            'snooze',
            '稍后提醒',
            '暂时静默，不再向我推送邮件/短信，静默期结束后按原策略继续提醒',
            ClockCircleOutlined,
            snoozeExtra,
          )}
          {renderOption(
            'close-only',
            '仅关闭横幅',
            '只收起本次横幅，不改变任何推送策略',
            EyeInvisibleOutlined,
          )}
        </Radio.Group>
      </Spin>

      <div className={styles['mute-modal-note']}>
        <InfoCircleOutlined className={styles['mute-modal-note-icon']} />
        该设置仅对当前管理员生效，同产品其他管理员仍会正常收到提醒。
      </div>
    </Modal>
  );
});

MuteModal.displayName = 'MuteModal';
